import React, { useEffect } from 'react'

import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Box, Chip, Divider, Grid, Paper, Typography } from '@material-ui/core'
import { isEmpty } from 'lodash'

import useFetch from 'hooks/useFetch'

import * as service from 'service'
import constants from 'constants/index'
import useStyles from './styles'

const { OPERATION_MODE_LABELS, ALERT_CLASS, ALERT_ICON } = constants.aiInventory

const SummaryField = ({ label, children }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>
      {label}
    </Typography>
    {children}
  </Box>
)

const EnumChip = ({ label }) => {
  const classes = useStyles()
  if (!label) return <Typography variant="body1">-</Typography>
  return <Chip size="small" label={label} className={classes.enumChip} />
}

const Summary = ({
  aiInventory,
  stepLabel,
  isActive,
  onRefresh,
  guestMode = false,
}) => {
  const classes = useStyles()
  const { RISK_ALERTS } = constants.aiInventory

  useEffect(() => {
    if (isActive && onRefresh) onRefresh()
  }, [isActive]) // eslint-disable-line react-hooks/exhaustive-deps

  const riskOptionsService = guestMode
    ? service.guestPortal.aiInventoryRiskOptions.get
    : service.dponet.aiInventoryRiskOptions.get

  const { response: riskOptionsResponse } = useFetch(
    riskOptionsService,
    undefined,
    [],
  )
  const allRiskOptions = riskOptionsResponse?.data?.aiInventoryRiskOptions || []

  if (!aiInventory) return null

  const selectedRiskOptionIds = new Set(aiInventory.riskOptionIds || [])
  const selectedRiskOptions = allRiskOptions.filter((option) =>
    selectedRiskOptionIds.has(option.id),
  )
  const childQuestions = allRiskOptions
    .filter(
      (option) =>
        selectedRiskOptionIds.has(option.id) && !isEmpty(option.children),
    )
    .flatMap((option) => option.children)

  const tool = aiInventory.aiInventoryTool
  const provider = tool?.aiInventoryProvider
  const departments = aiInventory.departments || []
  const purposes = aiInventory.aiInventoryPurposes || []
  const risk = aiInventory.risk
  const alertInfo = risk ? RISK_ALERTS[risk] : null
  const AlertIcon = risk ? ALERT_ICON[risk] : null

  const humanInterventionLabel =
    aiInventory.humanIntervention === true
      ? 'Sim'
      : aiInventory.humanIntervention === false
      ? 'Não'
      : null

  const personalDataLabel =
    aiInventory.processesPersonalData === true
      ? 'Sim'
      : aiInventory.processesPersonalData === false
      ? 'Não'
      : null

  return (
    <Paper variant="outlined">
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="overline">
            <strong>RESUMO</strong>
          </Typography>
          {stepLabel && (
            <Typography variant="subtitle1">{stepLabel}</Typography>
          )}
        </Box>
        <Typography variant="body2" color="textSecondary">
          Confira as informações cadastradas antes de concluir.
        </Typography>
      </Box>

      <Divider />

      <Box p={2} display="flex" flexDirection="column" gridGap={12}>
        {/* Informações Gerais */}
        <Box className={classes.sectionCard}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Informações gerais
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Identificação da IA e departamento responsável
          </Typography>
          <Divider className={classes.divider} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SummaryField label="Nome da IA">
                <Box display="flex" alignItems="center" gridGap={8}>
                  {tool?.iconUrl && (
                    <img
                      src={tool.iconUrl}
                      alt={tool.name}
                      className={classes.toolIcon}
                    />
                  )}
                  <Typography variant="body1">
                    {aiInventory.name || '-'}
                  </Typography>
                </Box>
              </SummaryField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <SummaryField label="Distribuidor">
                <Box display="flex" alignItems="center" gridGap={8}>
                  {provider?.iconUrl && (
                    <img
                      src={provider.iconUrl}
                      alt={provider.name}
                      className={classes.toolIcon}
                    />
                  )}
                  <Typography variant="body1">
                    {provider?.name || '-'}
                  </Typography>
                </Box>
              </SummaryField>
            </Grid>

            <Grid item xs={12}>
              <SummaryField label="Departamento">
                <Typography variant="body1">
                  {aiInventory.allDepartments
                    ? 'Todos os departamentos'
                    : !isEmpty(departments)
                    ? departments
                        .map((department) => department.name)
                        .join(', ')
                    : '-'}
                </Typography>
              </SummaryField>
            </Grid>

            {aiInventory.description && (
              <Grid item xs={12}>
                <SummaryField label="Descrição de funcionalidades">
                  <Typography variant="body1">
                    {aiInventory.description}
                  </Typography>
                </SummaryField>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Finalidade */}
        <Box className={classes.sectionCard}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Finalidade
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Motivo de uso, tipo de operação e intervenção humana
          </Typography>
          <Divider className={classes.divider} />
          <Grid container spacing={2}>
            {!isEmpty(purposes) && (
              <Grid item xs={12}>
                <SummaryField label="Por qual motivo a IA é utilizada">
                  {purposes.map((purpose) => (
                    <Typography key={purpose.id} variant="body1">
                      • {purpose.description}
                    </Typography>
                  ))}
                </SummaryField>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <SummaryField label="Tipo de operação">
                <EnumChip
                  label={OPERATION_MODE_LABELS[aiInventory.operationMode]}
                />
              </SummaryField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <SummaryField label="Intervenção humana">
                <EnumChip label={humanInterventionLabel} />
              </SummaryField>
            </Grid>
          </Grid>
        </Box>

        {/* Detalhamento */}
        <Box className={classes.sectionCard}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>
            Detalhamento
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Dados pessoais e categorias de risco
          </Typography>
          <Divider className={classes.divider} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <SummaryField label="Utiliza dados pessoais">
                <EnumChip label={personalDataLabel} />
              </SummaryField>
            </Grid>

            {aiInventory.processesPersonalData === true && (
              <Grid item xs={12}>
                <SummaryField label="Categorias de risco selecionadas">
                  <Box display="flex" flexWrap="wrap" gridGap={8} mt={0.5}>
                    {!isEmpty(selectedRiskOptions) ? (
                      selectedRiskOptions.map((option) => (
                        <Chip
                          key={option.id}
                          size="small"
                          label={option.label}
                          className={classes.enumChip}
                        />
                      ))
                    ) : (
                      <Chip
                        size="small"
                        label="Nenhum dos anteriores"
                        className={classes.enumChip}
                      />
                    )}
                  </Box>
                </SummaryField>
              </Grid>
            )}

            {childQuestions.map((child) => (
              <Grid item xs={12} sm={6} key={child.id}>
                <SummaryField label={child.label}>
                  <EnumChip
                    label={selectedRiskOptionIds.has(child.id) ? 'Sim' : 'Não'}
                  />
                </SummaryField>
              </Grid>
            ))}
          </Grid>

          {!guestMode && alertInfo && AlertIcon && (
            <Box className={clsx(classes.alert, classes[ALERT_CLASS[risk]])}>
              <Box className={classes.alertHeader}>
                <AlertIcon size={18} className={classes.alertIcon} />
                <Typography variant="subtitle2" className={classes.alertTitle}>
                  {alertInfo.label}
                </Typography>
              </Box>
              <Typography variant="body2">{alertInfo.description}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

Summary.propTypes = {
  aiInventory: PropTypes.object,
  stepLabel: PropTypes.string,
  isActive: PropTypes.bool,
  onRefresh: PropTypes.func,
  guestMode: PropTypes.bool,
}

export default Summary
